// React
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

// Hooks
import useResponsiveQueries from '@/hooks/utils/useResponsiveQueries';

// Utils
import cn from '@/utils/cn';

// Assets
import LogoWhite from '@/assets/images/logo/logo-white.svg?react';

// Components
import Button from '@/components/atoms/Button/Button';
import { ColorThemeEnum } from '@/enum/ColorThemeEnum';
import BurgerMenu from './BurgerMenu/BurgerMenu';
import Menu from './Menu/Menu';

// Types
import { HeaderType } from './HeaderType';

// Styles
import styles from './Header.module.css';

interface HeaderProps {
  menus: HeaderType['menus'];
  cta: HeaderType['cta'];
}

function Header({ menus = null, cta = null }: HeaderProps) {
  const calculateOpacity = (scrollY: number): number => {
    const maxOpacity = 100;
    const minOpacity = 0;
    const maxScroll = 200;
    return Math.min(minOpacity + (scrollY / maxScroll) * (maxOpacity - minOpacity), maxOpacity);
  };

  const updateStyles = (opacity: number, scrolledMenu: boolean) => {
    const headerTextColor = scrolledMenu ? 'var(--neutral-252)' : 'var(--neutral-FFF)';
    document.documentElement.style.setProperty('--header-text-color', headerTextColor);
    document.documentElement.style.setProperty('--header-bg-opacity', `${opacity}%`);
    document.documentElement.style.setProperty(
      '--header-hover-animation',
      opacity >= 50 ? 'var(--orange)' : 'var(--yellow)',
    );
  };

  // States
  const [isMenuHiddenState, setStateIsMenuHiddenState] = useState(false);
  const [isMenuColoredState, setIsMenuColoredState] = useState(false);

  // Hooks
  const { isSmallDesktop, isSmallMobile } = useResponsiveQueries();

  // Handlers
  const onScroll = (setIsMenuHidden: (hidden: boolean) => void, setIsMenuColored: (colored: boolean) => void) => () => {
    const { scrollY } = window;

    const scrolled = scrollY > window.innerHeight - 50;
    setIsMenuHidden(scrolled);

    const opacity = calculateOpacity(scrollY);
    const scrolledMenu = opacity > 35;
    setIsMenuColored(scrolledMenu);
    updateStyles(opacity, scrolledMenu);
  };

  // Effects
  useEffect(() => {
    const scrollHandler = onScroll(setStateIsMenuHiddenState, setIsMenuColoredState);

    // Run the scroll logic once on initial render
    scrollHandler();

    window.addEventListener('scroll', scrollHandler);

    return () => {
      window.removeEventListener('scroll', scrollHandler);
    };
  }, []);

  return (
    <header className={styles.headerWrapper}>
      <div className={cn([styles.header])}>
        <Link to="/" className={styles.logo} title="ZOL" aria-label="ZOL">
          <LogoWhite />
        </Link>
        <div className={styles.headerContent}>
          {menus && !isSmallDesktop ? (
            <div className={styles.menu}>
              <Menu menu={menus} />
            </div>
          ) : null}

          {cta && !isSmallMobile ? (
            <Link to={cta.link || '/'} className={styles.buttonLink}>
              <Button
                size="small"
                color={isMenuColoredState ? ColorThemeEnum.ORANGE : ColorThemeEnum.YELLOW}
                variant={isSmallMobile ? 'filled' : 'bordered'}
                className={cn([styles.cta, 'ws-nowrap'])}
              >
                {cta.label}
              </Button>
            </Link>
          ) : null}

          {menus && isSmallDesktop ? (
            <div className={styles.burger}>
              <BurgerMenu menus={menus} isHeaderScrolled={isMenuHiddenState} />
            </div>
          ) : null}
        </div>
      </div>

      {cta && isSmallMobile ? (
        <Link to={cta.link || '/'} className={styles.buttonLink}>
          <Button
            size="small"
            color={isMenuHiddenState ? ColorThemeEnum.ORANGE : ColorThemeEnum.YELLOW}
            variant={isSmallMobile ? 'filled' : 'bordered'}
            className={cn([styles.cta, 'ws-nowrap'])}
          >
            {cta.label}
          </Button>
        </Link>
      ) : null}
    </header>
  );
}

export default Header;
